/* eslint-disable max-lines */
import {
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { Entity, EntityClassProperty } from '../../../../../types';
import { JupiterTheme } from '../../../../theme/types';
import ArrayInput from './InputFields/ArrayInput';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    inputContainer: {
      marginBottom: theme.spacing(1.5),
      width: '100%',
      display: 'flex',
    },
    input: {
      flex: 1,
      display: 'flex',
      fontWeight: 'normal',
      flexDirection: 'column',
      '& svg': {
        position: 'initial',
      },
    },
    helpIcon: {
      color: theme.palette.grey[400],
      marginBottom: theme.spacing(1),
    },
    adornment: {
      marginRight: 0,
      marginLeft: theme.spacing(0.5),
    },
    tooltip: {
      padding: theme.spacing(2),
      wordBreak: 'break-word',
    },
  })
);

export interface HookProps {
  onUpdateProperties: (key: string, value: any) => void;
}

interface Props extends HookProps {
  properties: EntityClassProperty[];
  entity: Entity;

  onDeleteProperty?: (property: EntityClassProperty) => void;
}

export const usePropertiesForm = ({ onUpdateProperties }: HookProps) => {
  const handlePropertyUpdate = (e: any, checked?: any) => {
    const value =
      checked !== undefined && typeof checked === 'boolean'
        ? checked
        : e.target.value;
    onUpdateProperties(e.target.name, value);
  };

  const canEdit = (property: EntityClassProperty, entity: Entity) => {
    return true;
  };
  return { canEdit, handlePropertyUpdate };
};

const PropertiesForm = ({
  properties,
  entity,
  onUpdateProperties,
  onDeleteProperty,
}: Props) => {
  const classes = useStyles();
  const { canEdit, handlePropertyUpdate } = usePropertiesForm({
    onUpdateProperties,
  });

  return (
    <div className={classes.formSection}>
      {properties.map((property, i) => (
        <div key={i} className={classes.inputContainer}>
          <FieldOfType
            property={property}
            entity={entity}
            handlePropertyUpdate={handlePropertyUpdate}
            canEdit={canEdit}
            onUpdateProperties={onUpdateProperties}
          />
          {onDeleteProperty && (
            <IconButton onClick={() => onDeleteProperty(property)}>
              <Close />
            </IconButton>
          )}
        </div>
      ))}
    </div>
  );
};

interface FieldOfTypeProps {
  property: EntityClassProperty;
  entity: Entity;
  handlePropertyUpdate: (e: any, checked?: any) => void;
  canEdit: (property: EntityClassProperty, entity: Entity) => boolean;
  onUpdateProperties: (key: string, value: any) => void;
}

const FieldOfType = (props: FieldOfTypeProps) => {
  if (props.property.items && props.property.items.enum) {
    return <SelectFieldProperty {...props} />;
  }
  switch (props.property.type) {
    case 'string':
      return <TextFieldProperty {...props} />;
    case 'boolean':
      return <BooleanFieldProperty {...props} />;
    case 'integer':
      return <NumberFieldProperty {...props} />;
    case 'number':
      return <NumberFieldProperty {...props} />;
    case 'array':
      return <ArrayInput {...props} />;
    default:
      return null;
  }
};

const SelectFieldProperty = ({
  property,
  entity,
  handlePropertyUpdate,
  canEdit,
}: FieldOfTypeProps) => {
  const classes = useStyles();
  const items = property.items?.enum;
  if (!items) return null;
  return (
    <TextField
      select={true}
      className={classes.input}
      label={property.name}
      InputLabelProps={{
        shrink:
          entity.properties[property.name] !== undefined &&
          entity.properties[property.name] !== '',
      }}
      value={entity.properties[property.name] || ''}
      onChange={handlePropertyUpdate}
      inputProps={{ name: property.name }}
      name={property.name}
      disabled={!canEdit(property, entity)}
      InputProps={{
        endAdornment: (
          <InputAdornment
            classes={{ root: classes.adornment }}
            position="start"
          >
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement="right"
              title={property.description}
            >
              <div>
                <HelpIcon className={classes.helpIcon} />
              </div>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    >
      {items.map((c, i) => (
        <MenuItem key={i} value={c}>
          {c}
        </MenuItem>
      ))}
    </TextField>
  );
};

const TextFieldProperty = ({
  property,
  entity,
  handlePropertyUpdate,
  canEdit,
}: FieldOfTypeProps) => {
  const classes = useStyles();
  return (
    <TextField
      value={entity.properties[property.name]}
      onChange={handlePropertyUpdate}
      name={property.name}
      label={property.name}
      InputLabelProps={{ shrink: entity.properties[property.name] }}
      className={classes.input}
      helperText={property.required ? 'Required' : ''}
      placeholder={'Enter ' + property.type}
      disabled={!canEdit(property, entity)}
      InputProps={{
        endAdornment: (
          <InputAdornment
            classes={{ root: classes.adornment }}
            position="start"
          >
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement="right"
              title={property.description}
            >
              <div>
                <HelpIcon className={classes.helpIcon} />
              </div>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
};

const NumberFieldProperty = ({
  property,
  entity,
  handlePropertyUpdate,
  canEdit,
}: FieldOfTypeProps) => {
  const classes = useStyles();
  return (
    <TextField
      value={entity.properties[property.name]}
      onChange={handlePropertyUpdate}
      name={property.name}
      type="number"
      label={property.name}
      InputLabelProps={{ shrink: entity.properties[property.name] }}
      className={classes.input}
      helperText={property.required ? 'Required' : ''}
      placeholder={'Enter ' + property.type}
      disabled={!canEdit(property, entity)}
      InputProps={{
        endAdornment: (
          <InputAdornment
            classes={{ root: classes.adornment }}
            position="start"
          >
            <Tooltip
              classes={{ tooltip: classes.tooltip }}
              placement="right"
              title={property.description}
            >
              <div>
                <HelpIcon className={classes.helpIcon} />
              </div>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
};

const BooleanFieldProperty = ({
  property,
  entity,
  handlePropertyUpdate,
  canEdit,
}: FieldOfTypeProps) => {
  const classes = useStyles();
  const toggle: any = (
    <Switch
      color="primary"
      value={entity.properties[property.name]}
      checked={entity.properties[property.name]}
      onChange={handlePropertyUpdate}
      disabled={!canEdit(property, entity)}
      name={property.name}
    />
  );
  return (
    <FormGroup row={true} className={classes.input}>
      <FormControlLabel control={toggle} label={property.name} />
      <FormHelperText>{property.required ? 'Required' : ''}</FormHelperText>
    </FormGroup>
  );
};

export default PropertiesForm;
